import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthorizationRequest, CaseDocument } from '@/lib/authorization/types';

export type CompletenessStatus = 'INCOMPLETE' | 'READY' | 'OVERRIDDEN';

export interface PacketItem {
  id: string;
  code: string;
  label: string;
  weight: number;
  isComplete: boolean;
  isMissing: boolean;
}

export interface CompletenessResult {
  score: number;
  status: CompletenessStatus;
  items: PacketItem[];
  missingItems: string[];
  canSubmit: boolean;
  isOverridden: boolean;
  overrideReason?: string;
  overrideBy?: string;
  overrideAt?: string;
}

// Document weights as specified
const DOCUMENT_WEIGHTS: Record<string, { weight: number; label: string }> = {
  clinical_note: { weight: 25, label: 'Clinical Note' },
  imaging_report: { weight: 20, label: 'Imaging Report' },
  conservative_care: { weight: 15, label: 'Conservative Care Summary' },
  claim_number_doi: { weight: 15, label: 'Claim Number / Date of Injury (BWC)' },
  diagnosis_list: { weight: 10, label: 'Diagnosis List' },
  procedure_details: { weight: 15, label: 'CPT/Modifiers + Site of Service + Laterality' },
};

// Map document types to weight categories
const DOCUMENT_TYPE_MAPPING: Record<string, string> = {
  'CLINICAL_NOTES': 'clinical_note',
  'OPERATIVE_NOTE': 'clinical_note',
  'H&P': 'clinical_note',
  'PROGRESS_NOTE': 'clinical_note',
  'MRI_REPORT': 'imaging_report',
  'XRAY_REPORT': 'imaging_report',
  'CT_REPORT': 'imaging_report',
  'IMAGING': 'imaging_report',
  'PT_RECORDS': 'conservative_care',
  'CONSERVATIVE_CARE': 'conservative_care',
  'INJECTION_RECORDS': 'conservative_care',
  'BWC_CLAIM': 'claim_number_doi',
  'DOI_VERIFICATION': 'claim_number_doi',
  'DIAGNOSIS': 'diagnosis_list',
};

export function calculateCompleteness(
  caseData: AuthorizationRequest | null,
  documents: CaseDocument[],
  requiredDocs: string[] = []
): CompletenessResult {
  if (!caseData) {
    return {
      score: 0,
      status: 'INCOMPLETE',
      items: [],
      missingItems: [],
      canSubmit: false,
      isOverridden: false,
    };
  }

  const items: PacketItem[] = [];
  let totalWeight = 0;
  let completedWeight = 0;
  const missingItems: string[] = [];

  // Build items from required docs and case data
  const uploadedDocTypes = new Set(
    documents.filter(d => d.is_uploaded).map(d => d.document_type.toUpperCase())
  );

  // Check each weight category
  Object.entries(DOCUMENT_WEIGHTS).forEach(([code, { weight, label }]) => {
    let isComplete = false;

    // Special check for procedure_details - derived from case data
    if (code === 'procedure_details') {
      const hasCpt = caseData.cpt_codes && caseData.cpt_codes.length > 0;
      const hasDiagnosis = caseData.icd10_codes && caseData.icd10_codes.length > 0;
      // Site of service comes from facility type or explicit field
      const hasSite = !!caseData.facility_id || !!caseData.facility;
      isComplete = hasCpt && hasDiagnosis && hasSite;
    }
    // Check for diagnosis_list - from case data
    else if (code === 'diagnosis_list') {
      isComplete = caseData.icd10_codes && caseData.icd10_codes.length > 0;
    }
    // Check for BWC-specific claim/DOI
    else if (code === 'claim_number_doi') {
      // Only required for BWC payers
      const isBWC = caseData.payer?.payer_id?.toUpperCase().includes('BWC') ||
                    caseData.payer?.name?.toUpperCase().includes('BWC');
      if (!isBWC) {
        // Not applicable - mark as complete
        isComplete = true;
      } else {
        // Check if BWC-related docs are uploaded
        isComplete = uploadedDocTypes.has('BWC_CLAIM') || uploadedDocTypes.has('DOI_VERIFICATION');
      }
    }
    // Check document uploads for other categories
    else {
      // Find matching document types
      const matchingTypes = Object.entries(DOCUMENT_TYPE_MAPPING)
        .filter(([, category]) => category === code)
        .map(([type]) => type);
      
      isComplete = matchingTypes.some(type => uploadedDocTypes.has(type));
      
      // Also check against required_docs from rules engine
      if (!isComplete && requiredDocs.length > 0) {
        const requiredForCategory = requiredDocs.filter(doc => {
          const mapped = DOCUMENT_TYPE_MAPPING[doc.toUpperCase()];
          return mapped === code;
        });
        // If this category is in required docs, check if any uploaded
        if (requiredForCategory.length > 0) {
          isComplete = requiredForCategory.some(doc => 
            documents.some(d => d.document_type.toUpperCase() === doc.toUpperCase() && d.is_uploaded)
          );
        }
      }
    }

    totalWeight += weight;
    if (isComplete) {
      completedWeight += weight;
    } else {
      missingItems.push(label);
    }

    items.push({
      id: code,
      code,
      label,
      weight,
      isComplete,
      isMissing: !isComplete,
    });
  });

  const score = totalWeight > 0 ? Math.round((completedWeight / totalWeight) * 100) : 0;
  
  // Check override status from case data
  const isOverridden = (caseData as any).completeness_status === 'OVERRIDDEN';
  const overrideReason = (caseData as any).override_reason;
  const overrideBy = (caseData as any).override_by;
  const overrideAt = (caseData as any).override_at;

  let status: CompletenessStatus;
  if (isOverridden) {
    status = 'OVERRIDDEN';
  } else if (score >= 100) {
    status = 'READY';
  } else {
    status = 'INCOMPLETE';
  }

  return {
    score,
    status,
    items,
    missingItems,
    canSubmit: status === 'READY' || status === 'OVERRIDDEN',
    isOverridden,
    overrideReason,
    overrideBy,
    overrideAt,
  };
}

export function usePacketCompleteness(
  caseData: AuthorizationRequest | null,
  documents: CaseDocument[]
) {
  return useMemo(() => {
    const requiredDocs = (caseData as any)?.required_docs || [];
    return calculateCompleteness(caseData, documents, requiredDocs);
  }, [caseData, documents]);
}

// Check if current user can override
export async function canUserOverride(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  const { data, error } = await supabase.rpc('can_override_completeness', {
    _user_id: user.id,
  });

  if (error) {
    console.error('Error checking override permission:', error);
    return false;
  }

  return data === true;
}

// Apply override
export async function applyOverride(
  caseId: string,
  reason: string,
  logActivity: (action: string, details?: Record<string, unknown>) => Promise<void>
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { error } = await supabase
    .from('authorization_requests')
    .update({
      completeness_status: 'OVERRIDDEN',
      override_reason: reason,
      override_by: user.id,
      override_at: new Date().toISOString(),
    } as any)
    .eq('id', caseId);

  if (error) throw error;

  await logActivity('Override applied for incomplete packet', {
    reason,
    override_by: user.id,
  });
}
